import type { FC, ReactNode } from "react";
import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from "recharts";

type PieData = { name: string; value: number; color?: string };
type Counts = {
  ActiveCount?: number;
  InactiveCount?: number;
  activeCount?: number;
  inactiveCount?: number;
  [k: string]: any;
};

const PIE_COLORS = ["#0078D7", "#EC4899"];

const ProdCodePieChart: FC<{
  data?: PieData[];
  counts?: Counts | null;
  height?: number;
}> = ({ data, counts = null, height = 180 }) => {
  const chartData: PieData[] = (() => {
    if (Array.isArray(data) && data.length) {
      return data.map((d, i) => ({
        name: String(d.name ?? `Slice ${i}`),
        value: Number(d.value ?? 0),
        color: d.color ?? PIE_COLORS[i % PIE_COLORS.length],
      }));
    }
    if (counts) {
      const active = Number(counts.ActiveCount ?? counts.activeCount ?? 0);
      const inactive = Number(
        counts.InactiveCount ?? counts.inactiveCount ?? 0
      );
      return [
        {
          name: "Active",
          value: Number.isFinite(active) ? active : 0,
          color: PIE_COLORS[0],
        },
        {
          name: "Inactive",
          value: Number.isFinite(inactive) ? inactive : 0,
          color: PIE_COLORS[1],
        },
      ];
    }
    return [];
  })();

  if (!chartData.length) {
    return (
      <div
        className="h-full flex items-center justify-center text-xs text-gray-400"
        style={{ height }}
      >
        No data
      </div>
    );
  }

  const renderPieLabel = (props: any): React.ReactElement => {
    const {
      cx = 0,
      cy = 0,
      midAngle = 0,
      outerRadius = 0,
      percent = 0,
      value = 0,
    } = props || {};
    if (!percent || percent < 0.03) return <g />;
    const RAD = Math.PI / 180;
    const r =
      (typeof outerRadius === "number"
        ? outerRadius
        : Number.parseFloat(String(outerRadius)) || 0) + 14;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor={x > cx ? "start" : "end"}
        fontWeight={700}
        fontSize={11}
        fill="#111827"
      >
        {value} ({Math.round((percent ?? 0) * 100)}%)
      </text>
    );
  };

  const renderLabelLine = (props: any): React.ReactElement => {
    const {
      cx = 0,
      cy = 0,
      midAngle = 0,
      outerRadius = 0,
      index = 0,
      percent = 0,
    } = props || {};
    if (!percent || percent < 0.03) return <g />;
    const RAD = Math.PI / 180;
    const sx = cx + (outerRadius ?? 0) * Math.cos(-midAngle * RAD);
    const sy = cy + (outerRadius ?? 0) * Math.sin(-midAngle * RAD);
    const mx = cx + ((outerRadius ?? 0) + 8) * Math.cos(-midAngle * RAD);
    const my = cy + ((outerRadius ?? 0) + 8) * Math.sin(-midAngle * RAD);
    const ex = cx + ((outerRadius ?? 0) + 14) * Math.cos(-midAngle * RAD);
    const ey = cy + ((outerRadius ?? 0) + 14) * Math.sin(-midAngle * RAD);
    const sliceColor =
      chartData[index]?.color ?? PIE_COLORS[index % PIE_COLORS.length];
    return (
      <polyline
        points={`${sx},${sy} ${mx},${my} ${ex},${ey}`}
        stroke={sliceColor}
        strokeWidth={1}
        fill="none"
      />
    );
  };

  const renderTooltip = (props: {
    active?: boolean;
    payload?: any[];
    label?: string | number;
  }) => {
    const { active, payload, label } = props || {};
    if (!active || !payload?.length) return null;

    const item = payload[0] ?? {};
    let idx: number | undefined;
    if (typeof item?.payload?.index === "number") {
      idx = item.payload.index;
    } else if (typeof item?.index === "number") {
      idx = item.index;
    }

    const payloadName = item?.payload?.name ?? item?.name ?? label;
    const matched =
      typeof idx === "number" && chartData[idx]
        ? chartData[idx]
        : chartData.find((d) => d.name === payloadName);

    const valueRaw =
      matched?.value ?? Number(item?.payload?.value ?? item?.value ?? 0);
    const nameRaw =
      matched?.name ??
      item?.payload?.displayName ??
      item?.payload?.name ??
      item?.name ??
      label ??
      "Value";
    const color = matched?.color ?? item?.payload?.color ?? item?.color;
    return (
      <div
        className="bg-white border border-gray-200 rounded-md shadow-sm p-2"
        style={{ pointerEvents: "none", minWidth: 88 }}
      >
        <div className="text-lg font-semibold text-center">{valueRaw}</div>
        <div className="text-sm mt-1 text-center" style={{ color }}>
          {color ? (
            <span className="inline-flex items-center gap-2">
              <span
                className="h-3 w-3 rounded-full"
                style={{ background: color, display: "inline-block" }}
              />
              <span>{nameRaw}</span>
            </span>
          ) : (
            <span>{nameRaw}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            innerRadius={30}
            outerRadius={85}
            paddingAngle={2}
            cornerRadius={4}
            isAnimationActive
            stroke="#fff"
            strokeWidth={1}
            labelLine={renderLabelLine}
            label={renderPieLabel}
          >
            {chartData.map((entry, i) => (
              <Cell
                key={`${entry.name}-${i}`}
                fill={entry.color ?? PIE_COLORS[i % PIE_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={renderTooltip} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ProdCodePieChart;
